/**
 * The reader's settings — theme, time zone, rail layout and every table's
 * columns — as one store every consumer reads.
 *
 * **The backend holds the record. This browser holds a cache.** The settings
 * document follows the employee to any machine (ADR-0014). Each browser keeps
 * the last value it saw in the per-browser stores it already had, so the first
 * paint shows the reader's layout rather than the declared one and then
 * correcting it. Those stores are now written from here and nowhere else.
 *
 * The store works with no server at all. A screen test, and the signed-out
 * screen, read and write the cache alone. `PreferenceSync`, mounted in the
 * signed-in tree, is what joins it to the backend: it hands the store the
 * server's document with `adoptServerPreferences`, and a sender with
 * `connectPreferenceSender` for every later change.
 *
 * **A section the server holds wins. A section it lacks is uploaded** — from
 * the signed-in employee's own cache, or from a cache nobody owns yet, which is
 * one written before this store existed. That one rule is also the migration.
 * A cache that belongs to another employee is never uploaded; its sections
 * fall back to their defaults instead. A section is never removed on the
 * server once it exists — an emptied list is stored empty — so a stale browser
 * cannot bring back a choice the reader undid on another machine.
 *
 * **A change made before the server answers is not overwritten by that answer.**
 * The answer was read before the change, so it is older. Such a change is held
 * as dirty and uploaded instead of adopted.
 */

import {
  readColumnStore,
  writeColumnStore,
  type TableColumnStore,
  type TableKey
} from './column-preference-store.ts'
import {
  clearCollapsedGroups,
  clearCollapsedRailLists,
  clearHiddenPages,
  clearRecentPages,
  clearSectionOrder,
  clearStarredPages,
  readCollapsedGroups,
  readCollapsedRailLists,
  readHiddenPages,
  readRecentPages,
  readSectionOrder,
  readStarredPages,
  writeCollapsedGroups,
  writeCollapsedRailLists,
  writeHiddenPages,
  writeRecentPages,
  writeSectionOrder,
  writeStarredPages
} from './nav-group-store.ts'
import {
  readPreferenceOwner,
  writePreferenceOwner
} from './preference-owner-store.ts'
import type { TableColumnPreset } from './table-columns.ts'
import type { ThemePreference } from './theme.ts'
import {
  clearPreference,
  readPreference,
  writePreference
} from './theme-store.ts'
import {
  clearTimeZone,
  readTimeZone,
  writeTimeZone
} from './time-zone-store.ts'

/** Every setting the reader holds. A section that is absent was never chosen. */
export interface ViewerPreferences {
  theme?: ThemePreference
  timeZone?: string
  /**
   * Page ids an employee once hid. The rail no longer applies this list.
   * The field stays so an old document still loads.
   */
  hiddenPages?: readonly string[]
  /** Page ids the reader starred, in the order they starred them. */
  starredPages?: readonly string[]
  /** Page ids the reader opened, newest first. At most five. */
  recentPages?: readonly string[]
  /** Rail group names the reader closed. */
  collapsedGroups?: readonly string[]
  /** The rail lists the reader collapsed: `recent`, `tools`, or both. */
  collapsedRailLists?: readonly string[]
  sectionOrder?: readonly string[]
  /** One column layout per table. */
  tableColumns?: TableColumnStore
}

/**
 * One change to the settings, as `POST /preferences` accepts it.
 *
 * A sent section replaces the held one, except `tableColumns`, which merges
 * per table: a preset replaces that table's layout and `null` removes it.
 */
export interface PreferencePatch {
  theme?: ThemePreference
  timeZone?: string
  hiddenPages?: readonly string[]
  starredPages?: readonly string[]
  recentPages?: readonly string[]
  collapsedGroups?: readonly string[]
  collapsedRailLists?: readonly string[]
  sectionOrder?: readonly string[]
  tableColumns?: Readonly<Record<TableKey, TableColumnPreset | null>>
}

/**
 * Whether a change was held, and if not, where it stopped.
 *
 * `unsaved-browser` is a browser storing nothing, with no server to hold the
 * change either. `unsaved-service` is a server that did not take it; the
 * browser may still hold it, but the next load adopts the server's copy.
 */
export type SaveResult = 'saved' | 'unsaved-browser' | 'unsaved-service'

/** Sends one change to the server. Resolves false on any failure; never rejects. */
export type PreferenceSender = (patch: PreferencePatch) => Promise<boolean>

/** The sections that a patch replaces whole. */
const WHOLE_SECTIONS = [
  'theme',
  'timeZone',
  'hiddenPages',
  'starredPages',
  'recentPages',
  'collapsedGroups',
  'collapsedRailLists',
  'sectionOrder'
] as const

type WholeSection = (typeof WHOLE_SECTIONS)[number]

/** Each section as a parser answers it: the value, or null when unusable. */
type ParsedSections = {
  readonly [Section in keyof ViewerPreferences]-?: NonNullable<
    ViewerPreferences[Section]
  > | null
}

let snapshot: ViewerPreferences | null = null
let sender: PreferenceSender | null = null
const listeners = new Set<() => void>()
const dirtySections = new Set<WholeSection>()
const dirtyTables = new Set<TableKey>()

/**
 * Settings from parsed sections, leaving out every section that is null.
 *
 * The cache and the server answer both build settings this way, so a section
 * added here reaches both.
 *
 * @param parsed - Each section, or null when there is none to use.
 * @returns The settings.
 */
export function preferencesFrom(parsed: ParsedSections): ViewerPreferences {
  return Object.fromEntries(
    Object.entries(parsed).filter(([, value]) => value !== null)
  )
}

/**
 * Write the given sections to this browser's cache.
 *
 * @param sections - The sections to cache. An absent section is left alone.
 * @returns True when every section was held.
 */
function writeCache(sections: ViewerPreferences): boolean {
  let held = true
  if (sections.theme !== undefined) {
    writePreference(sections.theme)
  }
  if (sections.timeZone !== undefined) {
    writeTimeZone(sections.timeZone)
  }
  if (sections.hiddenPages !== undefined) {
    held = writeHiddenPages(sections.hiddenPages) && held
  }
  if (sections.starredPages !== undefined) {
    held = writeStarredPages(sections.starredPages) && held
  }
  if (sections.recentPages !== undefined) {
    held = writeRecentPages(sections.recentPages) && held
  }
  if (sections.collapsedGroups !== undefined) {
    held = writeCollapsedGroups(sections.collapsedGroups) && held
  }
  if (sections.collapsedRailLists !== undefined) {
    held = writeCollapsedRailLists(sections.collapsedRailLists) && held
  }
  if (sections.sectionOrder !== undefined) {
    held = writeSectionOrder(sections.sectionOrder) && held
  }
  if (sections.tableColumns !== undefined) {
    held = writeColumnStore(sections.tableColumns) && held
  }

  return held
}

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

/**
 * Apply one change to a set of settings.
 *
 * @param preferences - The settings before the change.
 * @param patch - The change.
 * @returns New settings. The input is not changed.
 */
export function applyPreferencePatch(
  preferences: ViewerPreferences,
  patch: PreferencePatch
): ViewerPreferences {
  const next: ViewerPreferences = { ...preferences }
  for (const section of WHOLE_SECTIONS) {
    if (patch[section] !== undefined) {
      // `Object.assign` with a computed key, because an indexed write through
      // a union of section names does not type-check without an assertion.
      Object.assign(next, { [section]: patch[section] })
    }
  }
  if (patch.tableColumns !== undefined) {
    const tables: Record<TableKey, TableColumnPreset> = {
      ...preferences.tableColumns
    }
    for (const [key, preset] of Object.entries(patch.tableColumns)) {
      if (preset === null) {
        delete tables[key]
      } else {
        tables[key] = preset
      }
    }
    next.tableColumns = tables
  }

  return next
}

/**
 * The settings in force.
 *
 * Read from the cache on first use, then held. The same object comes back
 * until a change, which is what `useSyncExternalStore` requires.
 *
 * @returns The settings.
 */
export function getPreferences(): ViewerPreferences {
  snapshot ??= preferencesFrom({
    theme: readPreference(),
    timeZone: readTimeZone(),
    hiddenPages: readHiddenPages(),
    starredPages: readStarredPages(),
    recentPages: readRecentPages(),
    collapsedGroups: readCollapsedGroups(),
    collapsedRailLists: readCollapsedRailLists(),
    sectionOrder: readSectionOrder(),
    tableColumns: readColumnStore()
  })

  return snapshot
}

/**
 * Be told when the settings change.
 *
 * @param listener - Called after every change.
 * @returns The unsubscribe function.
 */
export function subscribePreferences(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/**
 * Change the settings, cache the change, and send it when a server is joined.
 *
 * The change applies at once, whether or not it is then held. Undoing a
 * setting under the reader's hands is worse than one that did not persist,
 * so the result only says whether to tell them.
 *
 * @param patch - The change.
 * @returns Where the change stopped, if it did not hold.
 */
export async function updatePreferences(
  patch: PreferencePatch
): Promise<SaveResult> {
  const next = applyPreferencePatch(getPreferences(), patch)
  snapshot = next
  const cached = writeCache({
    ...patch,
    tableColumns:
      patch.tableColumns === undefined ? undefined : next.tableColumns
  })
  // Read before `emit`, so a listener that joins a sender cannot change which
  // path this change takes.
  const send = sender
  emit()

  if (send !== null) {
    return (await send(patch).catch(() => false)) ? 'saved' : 'unsaved-service'
  }

  for (const section of WHOLE_SECTIONS) {
    if (patch[section] !== undefined) {
      dirtySections.add(section)
    }
  }
  for (const key of Object.keys(patch.tableColumns ?? {})) {
    dirtyTables.add(key)
  }

  return cached ? 'saved' : 'unsaved-browser'
}

/**
 * Whether this browser's cache holds another employee's settings.
 *
 * A cache nobody owns is not another employee's: it predates the settings
 * document, and the first employee to sign in adopts it as their own.
 *
 * @param viewer - The signed-in address.
 * @returns True when the recorded owner is somebody else.
 */
export function cacheBelongsToAnother(viewer: string): boolean {
  const owner = readPreferenceOwner()

  return owner !== null && owner !== viewer
}

/**
 * The changes made while no sender was joined, as one patch.
 *
 * @returns Each dirty section as the store holds it now; a dirty table the
 *   store no longer holds is sent as `null`.
 */
function dirtyChanges(): PreferencePatch {
  const local = getPreferences()
  const patch: PreferencePatch = {}
  for (const section of dirtySections) {
    Object.assign(patch, { [section]: local[section] })
  }
  if (dirtyTables.size > 0) {
    patch.tableColumns = Object.fromEntries(
      [...dirtyTables].map(key => [key, local.tableColumns?.[key] ?? null])
    )
  }

  return patch
}

/**
 * Hand over the changes made while no sender was joined, and forget them.
 *
 * For a read that failed: there is no answer to weigh them against, and they
 * are the reader's latest choice, so they are sent as they stand.
 *
 * @returns The changes to send.
 */
export function takeDirtyChanges(): PreferencePatch {
  const patch = dirtyChanges()
  dirtySections.clear()
  dirtyTables.clear()

  return patch
}

/**
 * Drop another employee's cache, keeping only what this employee changed.
 *
 * Called as soon as the signed-in tree mounts, and again before an answer is
 * adopted. A change the new employee makes starts from what the store holds,
 * so a rail list still holding the previous employee's pages would carry them
 * into the new employee's document. Nothing happens for the employee's own
 * cache, or for a cache nobody owns.
 *
 * @param viewer - The signed-in address. The cache is recorded as theirs.
 */
export function releaseForeignCache(viewer: string): void {
  if (!cacheBelongsToAnother(viewer)) {
    return
  }
  const kept = applyPreferencePatch({}, dirtyChanges())
  snapshot = kept
  replaceCache(kept)
  writePreferenceOwner(viewer)
  emit()
}

/**
 * Adopt the server's settings, and say what the server lacks.
 *
 * @param server - The settings document the server answered with.
 * @param viewer - The signed-in address. This browser's cache is recorded as
 *   theirs once the answer is adopted.
 * @returns The change to send back: the sections the server lacks and this
 *   browser holds, and every change made before the server answered.
 */
export function adoptServerPreferences(
  server: ViewerPreferences,
  viewer: string
): PreferencePatch {
  // After this the cache is this employee's own, or one nobody owned.
  releaseForeignCache(viewer)
  const local = getPreferences()
  const next: ViewerPreferences = {}
  const upload: PreferencePatch = {}

  for (const section of WHOLE_SECTIONS) {
    if (server[section] !== undefined && !dirtySections.has(section)) {
      Object.assign(next, { [section]: server[section] })
    } else if (local[section] !== undefined) {
      Object.assign(next, { [section]: local[section] })
      Object.assign(upload, { [section]: local[section] })
    }
  }

  if (server.tableColumns !== undefined) {
    const tables: Record<TableKey, TableColumnPreset> = {
      ...server.tableColumns
    }
    const sent: Record<TableKey, TableColumnPreset | null> = {}
    for (const key of dirtyTables) {
      const preset = local.tableColumns?.[key]
      if (preset === undefined) {
        delete tables[key]
        sent[key] = null
      } else {
        tables[key] = preset
        sent[key] = preset
      }
    }
    next.tableColumns = tables
    if (Object.keys(sent).length > 0) {
      upload.tableColumns = sent
    }
  } else if (local.tableColumns && Object.keys(local.tableColumns).length > 0) {
    next.tableColumns = local.tableColumns
    upload.tableColumns = local.tableColumns
  }

  dirtySections.clear()
  dirtyTables.clear()
  snapshot = next
  replaceCache(next)
  writePreferenceOwner(viewer)
  emit()

  return upload
}

/**
 * Make the cache hold exactly these settings.
 *
 * A section they lack leaves the cache too, or the next load would read the
 * previous owner's value back as this employee's own.
 *
 * @param preferences - The settings to cache.
 */
function replaceCache(preferences: ViewerPreferences): void {
  writeCache(preferences)
  if (preferences.theme === undefined) {
    clearPreference()
  }
  if (preferences.timeZone === undefined) {
    clearTimeZone()
  }
  if (preferences.hiddenPages === undefined) {
    clearHiddenPages()
  }
  if (preferences.starredPages === undefined) {
    clearStarredPages()
  }
  if (preferences.recentPages === undefined) {
    clearRecentPages()
  }
  if (preferences.collapsedGroups === undefined) {
    clearCollapsedGroups()
  }
  if (preferences.collapsedRailLists === undefined) {
    clearCollapsedRailLists()
  }
  if (preferences.sectionOrder === undefined) {
    clearSectionOrder()
  }
  if (preferences.tableColumns === undefined) {
    writeColumnStore({})
  }
}

/**
 * Send every later change to the server.
 *
 * @param send - The sender.
 * @returns The function that disconnects it again, at sign-out.
 */
export function connectPreferenceSender(send: PreferenceSender): () => void {
  sender = send
  emit()

  return () => {
    if (sender === send) {
      sender = null
      emit()
    }
  }
}

/**
 * Whether the server's answer is in, and each change now goes to the server.
 *
 * A change the reader did not ask for, such as recording a visit, waits for
 * this. Made earlier, it is held as dirty and replaces the server's copy.
 *
 * @returns True once a sender is connected.
 */
export function isPreferenceSenderConnected(): boolean {
  return sender !== null
}

/**
 * Forget everything held in memory, so the next read comes from the cache.
 *
 * For tests: each one starts from what its own storage holds.
 */
export function resetPreferenceStore(): void {
  snapshot = null
  sender = null
  dirtySections.clear()
  dirtyTables.clear()
}
